import React, { useCallback, useEffect, useState, useRef } from 'react';

const MultiRangeSlider = ({ min, max, onChange, onChangeCommitted, isReset }) => {
    const [minVal, setMinVal] = useState(1901);
    const [maxVal, setMaxVal] = useState(2020);
    const minValRef = useRef(min);
    const maxValRef = useRef(max);
    const range = useRef(null);
    const commitOnly = typeof onChangeCommitted === 'function';

    // Convert to percentage
    const getPercent = useCallback(
        (value) => Math.round(((value - min) / (max - min)) * 100),
        [min, max]
    );

    // Set width of the range to decrease from the left side
    useEffect(() => {
        const minPercent = getPercent(minVal);
        const maxPercent = getPercent(maxValRef.current);

        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }

        if (isReset) {
            setMinVal(1901);
            setMaxVal(2020);
        }
    }, [minVal, getPercent, isReset]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        const minPercent = getPercent(minValRef.current);
        const maxPercent = getPercent(maxVal);

        if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [maxVal, getPercent]);

    useEffect(() => {
        if (commitOnly) return;
        onChange?.({ min: minVal, max: maxVal });
    }, [minVal, maxVal, onChange, commitOnly]);

    const emitCommitted = () => {
        const payload = { min: minValRef.current, max: maxValRef.current };
        if (commitOnly) {
            onChangeCommitted(payload);
        } else {
            onChangeCommitted?.(payload);
            onChange?.(payload);
        }
    };

    return (
        <div className="container relative">
            <input
                type="range"
                min={min}
                max={max}
                value={minVal}
                aria-label="Period from year"
                onChange={(event) => {
                    const value = Math.min(Number(event.target.value), maxVal - 2);
                    setMinVal(value);
                    minValRef.current = value;
                }}
                onMouseUp={emitCommitted}
                onTouchEnd={emitCommitted}
                className="thumb thumb--left"
                style={{ zIndex: minVal > max - 100 && "5" }}
                data-testid="main-period-from-year-input:range"
            />
            <input
                type="range"
                min={min}
                max={max}
                value={maxVal}
                aria-label="Period to year"
                onChange={(event) => {
                    const value = Math.max(Number(event.target.value), minVal + 1);
                    setMaxVal(value);
                    maxValRef.current = value;
                }}
                onMouseUp={emitCommitted}
                onTouchEnd={emitCommitted}
                className="thumb thumb--right"
                data-testid="main-period-to-year-input:range"
            />

            <div className="slider slider-white">
                <div className="slider__track bg-white" />
                <div className="slider__left-value">{minVal}</div>
                <div className="slider__right-value">{maxVal}</div>
            </div>
        </div>
    );
};

export default MultiRangeSlider;
